import { euro, num, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import Combobox from "@/components/Combobox";
import DatePickerField from "@/components/DatePickerField";
import ColumnFilter from "@/components/ColumnFilter";
import { Plus, Trash2, Check, X, HardHat } from "lucide-react";
import { NEW_TRATTA, NEW_AREA_CAVO, NONE } from "./constants";

const AnagSelect = ({ value, options, onChange, placeholder, testid }) => (
  <Combobox
    testid={testid}
    compact
    value={value || NONE}
    onChange={(v) => onChange(v === NONE ? "" : v)}
    placeholder={placeholder}
    emptyText="Vuoto (gestisci in Anagrafiche)"
    options={[{ value: NONE, label: "—" }, ...options.map((o) => ({ value: o.nome, label: o.nome }))]}
  />
);

const cellInput = "w-full h-7 px-1.5 text-[11px] bg-transparent focus:bg-blue-50 focus:ring-2 focus:ring-inset focus:ring-blue-700 focus:outline-none";

// Costruisce la configurazione colonne (header/filtro/cella/draft/footer) della
// griglia Contabilità. Ogni colonna è autonoma → ordine e larghezza modificabili.
// Stile compatto tipo foglio Excel (celle basse, senza padding).
export function useContabilitaColumns(ctx) {
  const {
    tratte, listino, squadre, competenze, areeCavo = [],
    filters, setF, mesiDisponibili, meseLabel,
    changeTratta, putRiga, updateQta, openSub, removeRiga,
    draftTratta, onDraftTrattaChange, newMode, newName, setNewName, confirmNewTratta, setNewMode,
    draftData, setDraftData, draftSquadra, setDraftSquadra, draftCompetenza, setDraftCompetenza,
    draftVoce, setDraftVoce, draftQta, setDraftQta, onDraftQtaKey, draftVoceObj, addRiga,
    draftAreaCavo, setDraftAreaCavo, draftNote, setDraftNote,
    newAreaCavoMode, setNewAreaCavoMode, newAreaCavoName, setNewAreaCavoName, confirmNewAreaCavo, onDraftAreaCavoChange,
    totale, totaleSub, hasActiveFilter, onClearFilters,
    totaleRiconosciuto = 0, canEditPagato = false,
  } = ctx;

  return [
    {
      id: "tratta", label: "Tratta", width: 168, sortField: "tratta",
      cellClassName: "p-0",
      filter: <ColumnFilter testid="filter-tratta" type="select" label="Tratta" value={filters.tratta} onChange={(v) => setF("tratta", v)} options={tratte.map((t) => ({ value: t.id, label: t.nome }))} />,
      cell: (r) => <Combobox compact testid={`riga-tratta-${r.id}`} value={r.tratta_id} onChange={(v) => changeTratta(r, v)} placeholder="Tratta…" options={tratte.map((t) => ({ value: t.id, label: t.nome }))} />,
      draft: () => (newMode ? (
        <div className="flex items-center gap-1 px-1">
          <input autoFocus value={newName} data-testid="new-tratta-inline-input"
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmNewTratta(); if (e.key === "Escape") { setNewMode(false); setNewName(""); } }}
            placeholder="Nome nuova tratta"
            className="w-full h-7 px-2 text-[11px] font-sans bg-white border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-700 focus:outline-none" />
          <button data-testid="confirm-new-tratta" onClick={confirmNewTratta} className="h-7 w-7 flex-shrink-0 rounded-md bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800"><Check className="h-3.5 w-3.5" /></button>
          <button onClick={() => { setNewMode(false); setNewName(""); }} className="h-7 w-7 flex-shrink-0 rounded-md text-slate-400 hover:text-rose-600 flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <Combobox compact testid="draft-tratta-select" value={draftTratta} onChange={onDraftTrattaChange} placeholder="Tratta…"
          options={[{ value: NEW_TRATTA, label: "➕ Nuova tratta…", className: "text-blue-700 font-medium" }, ...tratte.map((t) => ({ value: t.id, label: t.nome }))]} />
      )),
      draftClassName: "p-0",
    },
    {
      id: "data", label: "Data lavoro", width: 132, sortField: "data",
      cellClassName: "p-0",
      filter: <ColumnFilter testid="filter-mese" type="select" label="Data lavoro" value={filters.mese} onChange={(v) => setF("mese", v)} options={mesiDisponibili.map((m) => ({ value: m, label: meseLabel(m) }))} />,
      cell: (r) => <DatePickerField compact testid={`riga-data-${r.id}`} value={r.data} onChange={(v) => putRiga(r, { data: v })} />,
      draft: () => <DatePickerField compact testid="draft-data-input" value={draftData} onChange={setDraftData} />,
      draftClassName: "p-0",
    },
    {
      id: "squadra", label: "Squadra", width: 148, sortField: "squadra",
      cellClassName: "p-0",
      filter: <ColumnFilter testid="filter-squadra" type="select" label="Squadra" value={filters.squadra} onChange={(v) => setF("squadra", v)} options={squadre.map((s) => ({ value: s.nome, label: s.nome }))} />,
      cell: (r) => <AnagSelect testid={`riga-squadra-${r.id}`} value={r.squadra} options={squadre} placeholder="Squadra" onChange={(v) => putRiga(r, { squadra: v })} />,
      draft: () => <AnagSelect testid="draft-squadra-select" value={draftSquadra} options={squadre} placeholder="Squadra" onChange={setDraftSquadra} />,
      draftClassName: "p-0",
    },
    {
      id: "competenza", label: "Competenza", width: 148, sortField: "competenza",
      cellClassName: "p-0",
      filter: <ColumnFilter testid="filter-competenza" type="select" label="Competenza" value={filters.competenza} onChange={(v) => setF("competenza", v)} options={competenze.map((s) => ({ value: s.nome, label: s.nome }))} />,
      cell: (r) => <AnagSelect testid={`riga-competenza-${r.id}`} value={r.competenza} options={competenze} placeholder="Competenza" onChange={(v) => putRiga(r, { competenza: v })} />,
      draft: () => <AnagSelect testid="draft-competenza-select" value={draftCompetenza} options={competenze} placeholder="Competenza" onChange={setDraftCompetenza} />,
      draftClassName: "p-0",
    },
    {
      id: "codice", label: "VDC", width: 150, sortField: "codice",
      cellClassName: "p-0",
      filter: <ColumnFilter testid="filter-codice" type="text" label="VDC (Voce di capitolato)" value={filters.codice} onChange={(v) => setF("codice", v)} />,
      cell: (r) => (
        <Combobox compact testid={`riga-voce-${r.id}`} value={r.voce_id} disabled={r.pagato}
          onChange={(vid) => { const v = listino.find((x) => x.id === vid); if (v) putRiga(r, { voce_id: v.id, codice: v.codice, descrizione: v.descrizione, unita_misura: v.unita_misura, prezzo_unitario: v.prezzo_unitario }); }}
          placeholder="Voce…" emptyText="Listino vuoto"
          options={listino.map((v) => ({ value: v.id, label: `${v.codice} · ${v.descrizione}` }))} />
      ),
      draft: () => (
        <Combobox compact testid="draft-voce-select" value={draftVoce} onChange={setDraftVoce}
          placeholder="+ Voce…" emptyText="Listino vuoto"
          options={listino.map((v) => ({ value: v.id, label: `${v.codice} · ${v.descrizione} (${euro(v.prezzo_unitario)}/${v.unita_misura})` }))} />
      ),
      draftClassName: "p-0",
    },
    {
      id: "descrizione", label: "Descrizione", width: 220, sortField: "descrizione",
      cellClassName: "px-1.5 py-1 text-[11px] font-sans text-slate-800 truncate",
      filter: <ColumnFilter testid="filter-voce" type="text" label="Descrizione" value={filters.voce} onChange={(v) => setF("voce", v)} />,
      cell: (r) => r.descrizione,
      draft: () => <span className="text-slate-400 text-[11px] font-sans px-1.5">{draftVoceObj?.descrizione || "—"}</span>,
    },
    {
      id: "um", label: "UM", width: 60, sortField: "um",
      cellClassName: "px-1.5 py-1 text-center text-[11px] text-slate-500",
      filter: <ColumnFilter testid="filter-um" type="text" label="UM" value={filters.um} onChange={(v) => setF("um", v)} />,
      cell: (r) => r.unita_misura,
      draft: () => <span className="text-slate-500 text-[11px]">{draftVoceObj?.unita_misura || "—"}</span>,
    },
    {
      id: "prezzo", label: "Prezzo", width: 100, sortField: "prezzo",
      cellClassName: "px-1.5 py-1 text-right text-[11px] tabular-nums text-slate-600",
      filter: <ColumnFilter testid="filter-prezzo" type="number" label="Prezzo" value={filters.prezzo} onChange={(v) => setF("prezzo", v)} align="end" />,
      cell: (r) => euro(r.prezzo_unitario),
      draft: () => <span className="text-slate-500 text-[11px] tabular-nums">{draftVoceObj ? euro(draftVoceObj.prezzo_unitario) : "—"}</span>,
    },
    {
      id: "quantita", label: "Quantità", width: 100, sortField: "quantita",
      cellClassName: "text-right p-0",
      filter: <ColumnFilter testid="filter-quantita" type="number" label="Quantità" value={filters.quantita} onChange={(v) => setF("quantita", v)} align="end" />,
      cell: (r) => (
        <input type="number" step="0.01" defaultValue={r.quantita} key={r.quantita}
          data-testid={`qta-input-${r.id}`} disabled={r.pagato}
          title={r.pagato ? "Riga pagata: rimuovi il flag «Pagato» per modificarla" : undefined}
          onBlur={(e) => updateQta(r, e.target.value)}
          className={`${cellInput} text-right tabular-nums ${r.pagato ? "bg-slate-100 text-slate-400 cursor-not-allowed" : ""}`} />
      ),
      draft: () => (
        <input type="number" step="0.01" value={draftQta} data-testid="draft-qta-input"
          onChange={(e) => setDraftQta(e.target.value)} onKeyDown={onDraftQtaKey} placeholder="Quantità"
          className="w-full h-7 px-1.5 text-[11px] text-right tabular-nums bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-700 focus:outline-none" />
      ),
      draftClassName: "p-0.5",
    },
    {
      id: "importo", label: "Importo", width: 116, sortField: "importo",
      cellClassName: "px-1.5 py-1 text-right text-[11px] tabular-nums font-semibold text-slate-900",
      filter: <ColumnFilter testid="filter-importo" type="number" label="Importo" value={filters.importo} onChange={(v) => setF("importo", v)} align="end" />,
      cell: (r) => euro(r.importo),
      draft: () => <span className="text-slate-400 text-[11px] tabular-nums">{draftVoceObj ? euro((parseFloat(draftQta) || 0) * (draftVoceObj.prezzo_unitario || 0)) : "—"}</span>,
      draftClassName: "p-0.5 text-right",
      footer: () => (
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Totale commessa</div>
          <div data-testid="totale-commessa" className="font-heading font-bold text-blue-800 tabular-nums text-sm">{euro(totale)}</div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-600 mt-1">Riconosciuto (BEF)</div>
          <div data-testid="totale-riconosciuto" className="font-heading font-bold text-emerald-700 tabular-nums text-xs">{euro(totaleRiconosciuto)}</div>
        </div>
      ),
    },
    {
      id: "pagato", label: "Pagato", width: 84, sortField: "pagato",
      cellClassName: "text-center p-0",
      filter: <ColumnFilter testid="filter-pagato" type="select" label="Pagato" value={filters.pagato} onChange={(v) => setF("pagato", v)} options={[{ value: "si", label: "Sì" }, { value: "no", label: "No" }]} />,
      cell: (r) => (
        <div className="flex items-center justify-center h-7">
          <Checkbox data-testid={`pagato-${r.id}`} checked={!!r.pagato} disabled={!canEditPagato}
            onCheckedChange={(v) => putRiga(r, { pagato: !!v })}
            className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" />
        </div>
      ),
      draft: () => <span className="text-slate-300 text-[11px]">—</span>,
      draftClassName: "p-0.5 text-center",
    },
    {
      id: "sub", label: "Subappalto", width: 150, sortField: "sub",
      cellClassName: "text-right p-0.5",
      filter: <ColumnFilter testid="filter-sub" type="number" label="Subappalto" value={filters.sub} onChange={(v) => setF("sub", v)} align="end" />,
      cell: (r) => (
        <button data-testid={`sub-edit-${r.id}`} onClick={() => openSub(r)}
          className={`w-full h-7 px-1.5 rounded border text-[11px] font-sans flex items-center justify-center gap-1 transition-colors ${r.quantita_subappalto > 0 ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100" : "border-slate-200 bg-white text-slate-400 hover:border-blue-300 hover:text-blue-700"}`}>
          <HardHat className="h-3 w-3" />
          {r.quantita_subappalto > 0 ? <span className="tabular-nums">{num(r.quantita_subappalto)} · {euro(r.importo_sub)}</span> : <span>subappalto</span>}
        </button>
      ),
      draft: () => <span className="text-slate-300 text-[11px]">—</span>,
      draftClassName: "p-0.5 text-center",
      footer: () => (
        <div className="text-right">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-amber-600">Subappalto</div>
          <div data-testid="totale-subappalto" className="font-heading font-bold text-amber-700 tabular-nums text-xs">{euro(totaleSub)}</div>
        </div>
      ),
    },
    {
      id: "inserito", label: "Inserito da", width: 120, sortField: "inserito",
      cellClassName: "px-1.5 py-1 text-[11px] text-slate-500 truncate",
      cell: (r) => <span title={r.created_by || ""}>{r.created_by || "—"}</span>,
      draft: () => <span className="text-slate-300 text-[11px]">—</span>,
      draftClassName: "p-0.5 text-center",
    },
    {
      id: "inseritoIl", label: "Inserito il", width: 170, sortField: "inseritoIl",
      cellClassName: "px-1.5 py-1 text-[11px] text-slate-500 tabular-nums whitespace-nowrap",
      cell: (r) => (r.created_at ? formatDateTime(r.created_at) : "—"),
      draft: () => <span className="text-slate-300 text-[11px]">—</span>,
      draftClassName: "p-0.5 text-center",
    },
    {
      id: "areaCavo", label: "Area", width: 150, sortField: "areaCavo",
      cellClassName: "p-0",
      filter: <ColumnFilter testid="filter-area-cavo" type="select" label="Area" value={filters.areaCavo} onChange={(v) => setF("areaCavo", v)} options={areeCavo.map((a) => ({ value: a.nome, label: a.nome }))} />,
      cell: (r) => <AnagSelect testid={`riga-area-cavo-${r.id}`} value={r.area_cavo} options={areeCavo} placeholder="Area" onChange={(v) => putRiga(r, { area_cavo: v })} />,
      draft: () => (newAreaCavoMode ? (
        <div className="flex items-center gap-1 px-1">
          <input autoFocus value={newAreaCavoName} data-testid="new-area-cavo-inline-input"
            onChange={(e) => setNewAreaCavoName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmNewAreaCavo(); if (e.key === "Escape") { setNewAreaCavoMode(false); setNewAreaCavoName(""); } }}
            placeholder="Nome nuova area"
            className="w-full h-7 px-2 text-[11px] font-sans bg-white border border-blue-300 rounded-md focus:ring-2 focus:ring-blue-700 focus:outline-none" />
          <button data-testid="confirm-new-area-cavo" onClick={confirmNewAreaCavo} className="h-7 w-7 flex-shrink-0 rounded-md bg-blue-700 text-white flex items-center justify-center hover:bg-blue-800"><Check className="h-3.5 w-3.5" /></button>
          <button onClick={() => { setNewAreaCavoMode(false); setNewAreaCavoName(""); }} className="h-7 w-7 flex-shrink-0 rounded-md text-slate-400 hover:text-rose-600 flex items-center justify-center"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <Combobox compact testid="draft-area-cavo-select" value={draftAreaCavo} onChange={onDraftAreaCavoChange} placeholder="Area…"
          options={[{ value: NEW_AREA_CAVO, label: "➕ Nuova area…", className: "text-blue-700 font-medium" }, ...areeCavo.map((a) => ({ value: a.nome, label: a.nome }))]} />
      )),
      draftClassName: "p-0",
    },
    {
      id: "note", label: "Note", width: 190, sortField: "note",
      cellClassName: "p-0",
      cell: (r) => (
        <input defaultValue={r.note || ""} data-testid={`riga-note-${r.id}`} key={r.note}
          onBlur={(e) => putRiga(r, { note: e.target.value })} placeholder="Note…"
          className={`${cellInput} font-sans`} />
      ),
      draft: () => (
        <input value={draftNote} data-testid="draft-note-input"
          onChange={(e) => setDraftNote(e.target.value)} placeholder="Note…"
          className={`${cellInput} font-sans`} />
      ),
      draftClassName: "p-0",
    },
    {
      id: "azioni", label: "", width: 48, reorderable: false, resizable: false,
      cellClassName: "px-1 py-1 text-center",
      filter: hasActiveFilter ? (
        <button data-testid="clear-filters" onClick={onClearFilters} className="text-slate-400 hover:text-rose-600" aria-label="Azzera filtri" title="Azzera tutti i filtri">
          <X className="h-4 w-4 mx-auto" />
        </button>
      ) : null,
      cell: (r) => (
        <button data-testid={`delete-riga-${r.id}`} onClick={() => removeRiga(r.id)} className="text-slate-400 hover:text-rose-600 transition-colors">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      ),
      draft: () => (
        <Button size="icon" data-testid="add-riga-button" onClick={addRiga} className="h-7 w-7 bg-blue-700 hover:bg-blue-800">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      ),
      draftClassName: "p-0.5 text-center",
    },
  ];
}
